"use client";

import useEmblaCarousel from "embla-carousel-react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  navigationClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";

/**
 * Success Stories variant 2 — Student Showcase (ASU-009).
 *
 * Figma (Atlas Website, file PdDGTiH65FKjDxz4cIVllK):
 *   1750 12178:1029 · 1430 12169:528 · 1230 12170:782 · 1080 12174:903 · mobile 12208:18937
 *
 * Heading + subtitle + a carousel of image cards. Each card is a single
 * uploaded image (name / role / logo / photo baked in — nothing coded inside).
 * Desktop (≥1080): a paged 4-column grid (up to 8 cards / page) with arrows +
 * dot pagination shown only when there is more than one page. Mobile (<1080): a
 * single-row peek carousel with NO navigation. Breakpoints map 1:1 to the theme:
 * base = mobile, sm = 1080, md = 1230, lg = 1430, xl = 1750.
 */

const CARDS_PER_PAGE = 8; // desktop grid: 4 columns × 2 rows
const MAX_CARDS = 100; // editor enforces min 2 / max 100; cap defensively here too

type CardRow = {
  id?: string;
  image?: string;
  alt_image?: string;
  priority?: number;
  status?: boolean;
};

interface StudentShowcaseData {
  title?: string;
  title_text_color?: string;
  subtitle?: string;
  subtitle_text_color?: string;
  background_color?: string;
  left_navigation_icon?: string;
  left_navigation_icon_alt?: string;
  right_navigation_icon?: string;
  right_navigation_icon_alt?: string;
  indicator_color?: string;
  metadata?: CardRow[];
}

interface Props {
  data?: {
    data?: StudentShowcaseData;
  };
}

const isOn = (v: unknown) => v === true || v === 1 || v === "1";

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/** Default carousel arrow — Vuesax bold circle-arrow (lime). */
const ArrowCircleIcon = ({
  className,
  style,
}: {
  className?: string;
  style?: CSSProperties;
}) => (
  <svg
    className={className}
    style={style}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16.03 12.53L13.03 15.53C12.88 15.68 12.69 15.75 12.5 15.75C12.31 15.75 12.12 15.68 11.97 15.53C11.68 15.24 11.68 14.76 11.97 14.47L13.69 12.75H8.5C8.09 12.75 7.75 12.41 7.75 12C7.75 11.59 8.09 11.25 8.5 11.25H13.69L11.97 9.53C11.68 9.24 11.68 8.76 11.97 8.47C12.26 8.18 12.74 8.18 13.03 8.47L16.03 11.47C16.32 11.76 16.32 12.24 16.03 12.53Z"
      fill="currentColor"
    />
  </svg>
);

/** One card = a single uploaded image; fixed height per breakpoint, object-cover. */
const Card = ({ card }: { card: CardRow }) =>
  card.image ? (
    <div className="h-[180px] w-full overflow-hidden rounded-[16px] md:h-[190px] lg:h-[210px] xl:h-[240px]">
      <img
        src={card.image}
        alt={card.alt_image || ""}
        loading="lazy"
        className="pointer-events-none size-full object-cover"
      />
    </div>
  ) : null;

const StudentShowcase = ({ data }: Props) => {
  const isMobile = useIsMobile(1080);
  const ref = useRef<HTMLElement>(null);

  const {
    title = "",
    title_text_color = "#DDE567",
    subtitle = "",
    subtitle_text_color = "#FFFFFF",
    background_color = "#081F3D",
    left_navigation_icon,
    left_navigation_icon_alt = "Previous",
    right_navigation_icon,
    right_navigation_icon_alt = "Next",
    indicator_color = "#DDE567",
    metadata = [],
  } = data?.data || {};

  const cards = useMemo(
    () =>
      (Array.isArray(metadata) ? metadata : [])
        .filter((c) => isOn(c?.status) && String(c?.image || "").trim())
        .sort((a, b) => Number(a?.priority ?? 0) - Number(b?.priority ?? 0))
        .slice(0, MAX_CARDS),
    [metadata],
  );

  const pages = useMemo(() => chunk(cards, CARDS_PER_PAGE), [cards]);
  const showNavigation = pages.length > 1;

  // Mobile: one row when ≤5 cards, otherwise split equally into two rows. Each
  // "column" (one embla slide) stacks its top-row card over its bottom-row card.
  const mobileColumns = useMemo<CardRow[][]>(() => {
    if (cards.length > 5) {
      const topCount = Math.ceil(cards.length / 2);
      return Array.from({ length: topCount }, (_, i) =>
        [cards[i], cards[i + topCount]].filter((c): c is CardRow => Boolean(c)),
      );
    }
    return cards.map((card) => [card]);
  }, [cards]);

  // Desktop: page-by-page (each slide is a 4×2 grid). Mobile: card-by-card peek.
  const [desktopRef, desktopApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    slidesToScroll: 1,
  });
  const [mobileRef, mobileApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    containScroll: "trimSnaps",
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const [mobileSnaps, setMobileSnaps] = useState<number[]>([]);
  const [mobileSelected, setMobileSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (!desktopApi) return;
    setSelectedIndex(desktopApi.selectedScrollSnap());
    setCanPrev(desktopApi.canScrollPrev());
    setCanNext(desktopApi.canScrollNext());
  }, [desktopApi]);

  useEffect(() => {
    if (!desktopApi) return;
    onSelect();
    desktopApi.on("select", onSelect);
    desktopApi.on("reInit", onSelect);
    return () => {
      desktopApi.off("select", onSelect);
      desktopApi.off("reInit", onSelect);
    };
  }, [desktopApi, onSelect]);

  // Re-measure when the page count or layout (mobile↔desktop) changes.
  useEffect(() => {
    desktopApi?.reInit();
  }, [desktopApi, pages.length, isMobile]);

  // Mobile dot indicators track the embla scroll snaps.
  useEffect(() => {
    if (!mobileApi) return;
    const update = () => {
      setMobileSnaps(mobileApi.scrollSnapList());
      setMobileSelected(mobileApi.selectedScrollSnap());
    };
    update();
    mobileApi.on("select", update);
    mobileApi.on("reInit", update);
    return () => {
      mobileApi.off("select", update);
      mobileApi.off("reInit", update);
    };
  }, [mobileApi]);

  useEffect(() => {
    mobileApi?.reInit();
  }, [mobileApi, mobileColumns.length, isMobile]);

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) FireWidgetViewedEvent(ref, "student_showcase_section");
  // }, []);

  const scrollPrev = useCallback(() => {
    if (!desktopApi) return;
    desktopApi.scrollPrev();
    navigationClick({
      section: "student_showcase_section",
      component: "carousel_navigation",
      label: "previous",
      index: String(desktopApi.selectedScrollSnap()),
    });
  }, [desktopApi]);

  const scrollNext = useCallback(() => {
    if (!desktopApi) return;
    desktopApi.scrollNext();
    navigationClick({
      section: "student_showcase_section",
      component: "carousel_navigation",
      label: "next",
      index: String(desktopApi.selectedScrollSnap()),
    });
  }, [desktopApi]);

  const scrollTo = useCallback(
    (index: number) => {
      desktopApi?.scrollTo(index);
    },
    [desktopApi],
  );

  if (cards.length === 0) return null;

  return (
    <section
      ref={ref}
      style={{ backgroundColor: background_color }}
      className={cn(
        "flex w-full flex-col gap-[24px] py-[40px]",
        "sm:gap-[32px] sm:py-[28px]",
        "md:gap-[40px] md:py-[32px]",
        "lg:gap-[44px] lg:py-[40px]",
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex w-full flex-col gap-[14px] px-[24px]",
          "sm:gap-[18px] sm:px-[40px] md:px-[60px] lg:px-[80px] xl:px-[180px]",
        )}
      >
        {title && (
          <h2
            className={cn(
              "font-poppins font-bold leading-[1.2] tracking-[-0.02em] lg:leading-[1.15]",
              "line-clamp-2 sm:line-clamp-1",
              "text-[28px] sm:text-[36px] md:text-[40px] lg:text-[48px] xl:text-[56px]",
            )}
            style={{ color: title_text_color }}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p
            className={cn(
              "font-poppins font-normal leading-[1.6] xl:leading-[1.55]",
              "line-clamp-4 sm:line-clamp-2",
              "text-[14px] sm:text-[18px] lg:text-[20px] xl:text-[22px]",
            )}
            style={{ color: subtitle_text_color }}
          >
            {subtitle}
          </p>
        )}
      </div>

      {isMobile ? (
        /* Mobile: peek carousel with dot indicators (no prev/next buttons).
           One row when ≤5 cards, otherwise two equal rows. Bleeds to the right. */
        <>
          <div className="overflow-hidden pl-[24px]" ref={mobileRef}>
            <div className="flex gap-[16px]">
              {mobileColumns.map((column, columnIndex) => (
                <div key={columnIndex} className="min-w-0 flex-[0_0_72%]">
                  <div className="flex flex-col gap-[16px]">
                    {column.map((card, rowIndex) => (
                      <Card
                        key={card.id ?? `${columnIndex}-${rowIndex}`}
                        card={card}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {mobileSnaps.length > 1 && (
            <div className="flex items-center justify-center gap-[5px]">
              {mobileSnaps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => mobileApi?.scrollTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={cn(
                    "h-[10px] rounded-full transition-all",
                    i === mobileSelected ? "w-[40px]" : "w-[10px]",
                  )}
                  style={{
                    backgroundColor: indicator_color,
                    opacity: i === mobileSelected ? 1 : 0.5,
                  }}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        /* Desktop: paged 4-column grid carousel. */
        <>
          {/* Horizontal padding lives on this wrapper, NOT on the embla
              viewport — `overflow-hidden` clips at the border box, so padding on
              the viewport itself would expose the next page's slide inside the
              right-padding strip (a partial extra card). */}
          <div className="px-[40px] md:px-[60px] lg:px-[80px] xl:px-[180px]">
            <div className="overflow-hidden" ref={desktopRef}>
              {/* Gap between page slides matches the in-grid gap, so the gutter
                  shows during the page transition. embla v8 measures flex `gap`,
                  so each page still snaps flush to the viewport edge. */}
              <div className="flex gap-[16px] md:gap-[24px] xl:gap-[32px]">
                {pages.map((page, pageIndex) => (
                  <div key={pageIndex} className="min-w-0 flex-[0_0_100%]">
                    <div className="grid grid-cols-4 gap-[16px] md:gap-[24px] xl:gap-[32px]">
                      {page.map((card, i) => (
                        <Card key={card.id ?? `${pageIndex}-${i}`} card={card} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {showNavigation && (
            <div className="flex w-full items-center justify-center gap-[24px] px-[40px] md:px-[60px] lg:px-[80px] xl:px-[180px]">
              <button
                type="button"
                onClick={scrollPrev}
                disabled={!canPrev}
                aria-label={left_navigation_icon_alt}
                className="shrink-0 cursor-pointer transition-opacity disabled:cursor-default disabled:opacity-40"
              >
                {left_navigation_icon ? (
                  <img
                    src={left_navigation_icon}
                    alt={left_navigation_icon_alt}
                    className="size-[24px] rotate-180 object-contain"
                  />
                ) : (
                  <ArrowCircleIcon
                    className="size-[24px] rotate-180"
                    style={{ color: indicator_color }}
                  />
                )}
              </button>

              <div className="flex items-center gap-[5px]">
                {pages.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => scrollTo(i)}
                    aria-label={`Go to page ${i + 1}`}
                    className={cn(
                      "h-[10px] rounded-full transition-all",
                      i === selectedIndex ? "w-[40px]" : "w-[10px]",
                    )}
                    style={{
                      backgroundColor: indicator_color,
                      opacity: i === selectedIndex ? 1 : 0.5,
                    }}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={scrollNext}
                disabled={!canNext}
                aria-label={right_navigation_icon_alt}
                className="shrink-0 cursor-pointer transition-opacity disabled:cursor-default disabled:opacity-40"
              >
                {right_navigation_icon ? (
                  <img
                    src={right_navigation_icon}
                    alt={right_navigation_icon_alt}
                    className="size-[24px] object-contain"
                  />
                ) : (
                  <ArrowCircleIcon
                    className="size-[24px]"
                    style={{ color: indicator_color }}
                  />
                )}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default StudentShowcase;
